import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState, StatCard } from "@/components/shared";
import { Link2 } from "lucide-react";
import { toast } from "sonner";

export default function InternalLinks() {
  const { activeWebsite } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (!activeWebsite) { setLoading(false); return; }
      setLoading(true);
      try {
        const { data: d } = await api.get(`/websites/${activeWebsite.id}/linkgraph`);
        setData(d);
      } catch (e) { toast.error(errMsg(e)); }
      setLoading(false);
    })();
  }, [activeWebsite]);

  if (loading) return <Skeleton className="h-64 w-full" />;
  if (!data || data.total_pages === 0) {
    return <EmptyState icon={Link2} title="No link graph yet" message="Run a website audit to build the internal link graph." testId="internal-links-empty" />;
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold tracking-tight">Internal Links</h1>
        <p className="text-xs text-muted-foreground">Link graph analysis: orphans, weak pages, depth and best-linked pages. Recommendations stay contextual - never anchor spam.</p>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Pages in graph" value={data.total_pages} testId="linkgraph-total-pages" />
        <StatCard label="Orphan pages" value={data.orphans.length} testId="linkgraph-orphans" />
        <StatCard label="Weakly linked (≤1)" value={data.weak_pages.length} testId="linkgraph-weak" />
        <StatCard label="Deep pages (4+ clicks)" value={data.deep_pages.length} testId="linkgraph-deep" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="shadow-none">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Most linked pages</CardTitle></CardHeader>
          <CardContent className="scc-table-wrap p-0">
            <Table>
              <TableHeader><TableRow><TableHead>URL</TableHead><TableHead className="text-right">In</TableHead><TableHead className="text-right">Out</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.most_linked.map((p) => (
                  <TableRow key={p.url}>
                    <TableCell className="max-w-[280px] truncate font-mono text-xs">{p.url}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{p.incoming_links}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{p.outgoing_links}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="shadow-none">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Needs internal-link support</CardTitle></CardHeader>
          <CardContent className="space-y-2 p-3">
            {data.orphans.length === 0 && data.weak_pages.length === 0 && (
              <p className="text-xs text-muted-foreground">No orphan or weakly-linked pages. Good structure.</p>
            )}
            {data.orphans.map((p) => (
              <div key={p.url} className="flex items-center gap-2 rounded-md border p-2" data-testid="orphan-page-row">
                <Badge variant="outline" className="border-rose-200 bg-rose-50 text-[10px] text-rose-700">orphan</Badge>
                <span className="truncate font-mono text-xs">{p.url}</span>
                <Badge variant="outline" className="ml-auto text-[10px]">{p.page_type}</Badge>
              </div>
            ))}
            {data.weak_pages.map((p) => (
              <div key={p.url} className="flex items-center gap-2 rounded-md border p-2" data-testid="weak-page-row">
                <Badge variant="outline" className="border-amber-200 bg-amber-50 text-[10px] text-amber-800">{p.incoming_links} link</Badge>
                <span className="truncate font-mono text-xs">{p.url}</span>
                <Badge variant="outline" className="ml-auto text-[10px]">{p.page_type}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
